import React from 'react';
import Resizer from "react-image-file-resizer";

import {storage} from "../../firebase/firebase";

import './PhotoUpload.css'
import {ClueNavButton, DELETE} from "../clues/ClueNavButton";

/**
 * Shows props.url if present and non-null, or "None" text.
 * If props.loading is true, shows loading state instead.
 *
 * Style if highly customized (static) for our usage.
 */
export const PhotoUpload = props => {
   const {url, loading, editable, deletedCallback, hideNoneText} = props;

   const hasImage = !!url;

   return (
      <div>
         {loading &&
         <div className="clear-all top-margin-half loader-container">
            <div className="little-loader"/>
         </div>
         }
         {!loading && hasImage &&
         <div className="clear-all top-margin-half picture-container">
            <div className="aspect-ratio__inner-wrapper">
               <img className="hunt-picture" src={url} alt="Hunt"/>
            </div>
            { editable &&
            <div className="pull-right remove-button">
               <ClueNavButton
                  text="Remove" type={DELETE} iconName="trash" index={0} nav={() => deletedCallback(null)}
                  isDanger={true}
               />
            </div>}
         </div>
         }
         {!loading && !hasImage && !hideNoneText &&
         <div className="clear-all user top-margin-half"> None </div>
         }
      </div>
   )
}

/**
 * Processes and uploads image.
 *
 * @param file image file to uploaded
 * @param imageProperty   name of the filebase property we want to store the final URL in.
 * @param thumbProperty   name of the filebase property we want to store the final thumb URL in.
 *                        LEAVE null to indicate no thumb.
 * @param gameId game id for storage of image
 * @param authorId author userid for storage of image
 * @param isPublic  whether game is stored in public or not.
 * @param callback  callback after photo done uploading and URL retrieved.
 * @param errorCallback  in case of error.  Includes message if any should be displayed to user.
 */
export function processFile(file,
                            imageProperty, thumbProperty = null,
                            gameId, authorId,
                            isPublic,
                            gameTime,
                            callback, errorCallback) {

   // todo: can use file.type for mime type of image/jpeg, image/png, image/gif (I think)
   if (file.name.endsWith("gif") || file.name.endsWith("GIF")) {
      // handle gif
      errorCallback("No support for Gifs **yet**...");
      return;
   }

   const imgType =  (file.name.endsWith("jpg") || file.name.endsWith("jpeg"))
      ? "JPEG"
      : (file.name.endsWith("webp")) ? "WEBP"
         :"PNG";

   const name = fromGameTime(gameTime, imgType)

   correctIPhoneImageRotationFromFile(file, function(rotation) {

      // High W-to-H ratios work well on Hunt game summary.
      Resizer.imageFileResizer(
         file,
         1200,
         1200,
         imgType,
         70,
         rotation,
         blob => {
            if (thumbProperty == null) {
               doUploads(name, blob, null, imageProperty, thumbProperty, gameId, authorId, isPublic, callback, errorCallback)
            } else {
               doThumb(file, rotation, name, imgType, blob, imageProperty, thumbProperty, gameId, authorId, isPublic, callback, errorCallback)
            }
         },
         'blob'
      );
   });
}

// from http://stackoverflow.com/a/32490603
// get orientation from file metadata.  If any, returns proper rotation for image in degrees.  If none, returns 0.
function correctIPhoneImageRotationFromFile(file, callback) {
   const reader = new FileReader();

   // Only try to rotate iPhone images as those are the only ones with problem.
   // Android portrait images will express a rotation, but we don't want to rotate those.
   // Better long term solution would be letting the user rotate image in the web app.
   var isiDevice = /ipad|iphone|ipod/i.test(navigator.userAgent.toLowerCase());
   if (!isiDevice) {
      return callback(0)
   }

   reader.onload = function(event) {

      if (!event.target || !event.target.result) return callback(0)
      let view = null;
      try {
         view = new DataView(event.target.result);
      } catch (e) {
         console.warn("Could not read image file");
      }
      if (!view) return callback (0)

      if (view.getUint16(0, false) !== 0xFFD8) return callback(0);

      const length = view.byteLength;
      let offset = 2;

      while (offset < length) {
         const marker = view.getUint16(offset, false);
         offset += 2;

         if (marker === 0xFFE1) {
            if (view.getUint32(offset += 2, false) !== 0x45786966) {
               return callback(0);
            }
            const little = view.getUint16(offset += 6, false) === 0x4949;
            offset += view.getUint32(offset + 4, little);
            var tags = view.getUint16(offset, little);
            offset += 2;

            for (let i = 0; i < tags; i++)
               if (view.getUint16(offset + (i * 12), little) === 0x0112)
                  return callback(rotationFromOrientation(view.getUint16(offset + (i * 12) + 8, little)));
         } else if ((marker & 0xFF00) !== 0xFF00) break;
         else offset += view.getUint16(offset, false);
      }
      return callback(0);
   };

   reader.readAsArrayBuffer(file.slice(0, 64 * 1024));
}

function rotationFromOrientation(orientation) {
   switch (orientation) {
      case 6: return 90;
      case 8: return 270;
      case 3: return 180;
      default: return 0;
   }
}

function fromGameTime(gameTime, imgType) {
   const creationDate = gameTime.toDate();
   const now = new Date();

   return "img" + Math.round((now.getTime() - creationDate.getTime())/5000).toString();
}

export function thumbName(name) {

   const index = name.lastIndexOf('_');
   if (index < 0) {
      return name + '_thumb';
   }

   const base = name.slice(0,index)
   return base.concat('_thumb');
}


/**
 * Make the thumb and pass everything along for upload.
 */
function doThumb(file, rotation, name, imgType, blob0, imageProperty, thumbProperty, gameId, authorId, isPublic, callback, errorCallback) {

      // High W-to-H ratios work well on Hunt game summary.
      Resizer.imageFileResizer(
         file,
         256,
         256,
         imgType,
         40,
         rotation,
         blobThumb => {
            doUploads(name, blob0, blobThumb, imageProperty, thumbProperty, gameId, authorId, isPublic, callback, errorCallback)
         },
         'blob'
      );
}


/**
 * This happens last.
 *
 * Do the upload for the main blog (image reduced to 1024).
 * On success, do the upload for the thumb blob.
 * After thumb success, do callback.
 *
 * Handle errors.
 *
 * @param name  the name of the file on local filesystem.  Used for uploaded name.  Thumb version prepends 'thumb_'
 * @param blob0      the image binary of the main image to upload.
 * @param blobThumb  the image binary of the thumb to upload.  Leave null for no thumb.
 * @param imageProperty   name of the filebase property we want to store the final URL in.
 * @param thumbProperty   name of the filebase property we want to store the final thumb URL in, if applicable.
 * @param gameId      gameId, used to make storage reference
 * @param authorId    authorId, used to make storage reference
 * @param isPublic    isPublic, used to make storage reference
 * @param callback    callback to pass back file references and property names.
 * @param errorCallback  callback in case of error.
 */
function doUploads(name, blob0, blobThumb, imageProperty, thumbProperty, gameId, authorId, isPublic, callback, errorCallback) {

   const thumbName = 'thumb_' + name;
   const ref0 = fbStorageRef(gameId, authorId, isPublic).child(name);
   const refThumb = !!blobThumb ? fbStorageRef(gameId, authorId, isPublic).child(thumbName) : null ;

   return doUploadsForRefs(ref0, refThumb, blob0, blobThumb, imageProperty, thumbProperty, callback, errorCallback)
}

function doUploadsForRefs(ref0, refThumb, blob0, blobThumb, imageProperty, thumbProperty, callback, errorCallback) {

   const uploadTask = ref0.put(blob0);

   // Listen for state changes, errors, and completion of the upload.
   uploadTask.on('state_changed', //storage.TaskEvent.STATE_CHANGED, // or 'state_changed'
      snapshot => {
         // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
         // let progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
         // console.log('Upload is ' + progress + '% done');
      },
      error => { handleFirebaseStorageError(error, errorCallback) },
      () => {
         uploadTask.snapshot.ref.getDownloadURL()
            .then( downloadURL => {

               if (refThumb == null) {
                  callback(downloadURL, imageProperty, null, null);
               } else {
                  const uploadTask2 = refThumb.put(blobThumb);
                  uploadTask2.on('state_changed',  snapshot => {
                        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                        // var progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        // console.log('Thumb Upload is ' + progress + '% done');
                     },
                     error => { handleFirebaseStorageError(error, errorCallback) },
                     () => {
                        uploadTask2.snapshot.ref.getDownloadURL()
                           .then( downloadURL2 => {
                              callback(downloadURL, imageProperty, downloadURL2, thumbProperty);
                           })
                           .catch( error => {
                              console.warn("getting thumb reference,  error:", error);
                              errorCallback("Error getting new image reference thumbnail.");
                           });
                     })
               }
            } )
            .catch( error => {
               console.warn("getting reference,  error:", error);
               errorCallback("Error getting new image reference.");
            } );
      }
   )
}

export function deleteGamePhotosDirInFbStorage(gameId, authorId, isPublic) {

   const storageRef = fbStorageRef(gameId, authorId, isPublic);

   // This call requires upgrade to security rules, v2.  :-(.  But worse, you can't just delete a directory; doesnt work  :-(
   storageRef.listAll()
      .then((listResult) => {
         listResult.items.forEach( (storageRef) => {
            storageRef.delete().then(r => {/*ignore*/ });
         } )
      } )
      .catch(function(error) {
         // meh
         console.log("error listing directory for deletion.:", error);
      } );
}

export function deleteFileInFbStorage(fullPath) {

   const storageRef = storage.refFromURL(fullPath);
   // Delete the file
   storageRef.delete()
      .then(function() { } )  // yay!
      .catch(function(error) {
         // meh
         console.log("error deleting file:", error);
      } );
}

// get the storage reference for a given gameId, authorId, and whether public or private
function fbStorageRef(gameId, authorId, isPublic) {
   // Create a storage reference from our storage service
   const storageRef = storage.ref();
   const gameLoc = isPublic ? "games" : "privateGames";
   return storageRef.child('users').child(authorId).child(gameLoc).child(gameId);
}

// convert firebase storage error into a string to display to the user. Or return null to not show an error message
function handleFirebaseStorageError(error, callback) {
   let errorMsg = null
   // A full list of error codes is available at
   // https://firebase.google.com/docs/storage/web/handle-errors
   switch (error.code) {
      case 'storage/unauthorized':
         // User doesn't have permission to access the object
         console.warn("storage unauthorized");
         errorMsg = "Error uploading image: storage unauthorized."
         break;
      case 'storage/canceled':
         // User canceled the upload
         break;
      case 'storage/unauthenticated':  //	User is unauthenticated, please authenticate and try again.
         errorMsg = "Failed - user logged out.  Log in and try again.";
         break;
      case 'storage/retry-limit-exceeded':  //	The maximum time limit on an operation (upload, download, delete, etc.) has been excceded. Try uploading again.
         errorMsg = "Uploading timed out.  Try again.";
         break;
      case 'storage/invalid-checksum':  //	File on the client does not match the checksum of the file received by the server. Try uploading again.
      case 'storage/cannot-slice-blob':  //	Commonly occurs when the local file has changed (deleted, saved again, etc.). Try uploading again after verifying that the file hasn't changed.
      case 'storage/server-file-wrong-size':  //	File on the client does not match the size of the file recieved by the server. Try uploading again.
         errorMsg = "Error uploading image.  Try again.";
         break;
      case 'storage/quota-exceeded':  //	Quota on your Cloud Storage bucket has been exceeded. If you're on the free tier, upgrade to a paid plan. If you're on a paid plan, reach out to Firebase support.
         console.warn("Fail due to Quota exceeding on firebase storage.");
         errorMsg = "Error uploading image. Contact support";
         break;
      case 'storage/bucket-not-found':   //	No bucket is configured for Cloud Storage
      case 'storage/project-not-found':  //	No project is configured for Cloud Storage
      case 'storage/invalid-event-name': //	Invalid event name provided. Must be one of [`running`, `progress`, `pause`]
      case 'storage/invalid-url':        //	Invalid URL provided to refFromURL(). Must be of the form: gs://bucket/object or https://firebasestorage.googleapis.com/v0/b/bucket/o/object?token=<TOKEN>
      case 'storage/invalid-argument':   //	The argument passed to put() must be `File`, `Blob`, or `UInt8` Array. The argument passed to putString() must be a raw, `Base64`, or `Base64URL` string.
      case 'storage/no-default-bucket':  //	No bucket has been set in your config's storageBucket property.
      case 'storage/unknown':
      case 'storage/object-not-found':
      default:
         // Unknown error occurred, inspect error.serverResponse
         console.warn("storage error", error);
         errorMsg = "Error uploading image: " + error.toString();
         break;
   }
   callback(errorMsg)
}



// WEBPACK FOOTER //
// ./src/components/image/PhotoUpload.js